import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { getServicesManager } from '../../../services'
import { InboundRoute } from '../../../interfaces/bot-config'

// Async thunks
export const fetchInboundRoutes = createAsyncThunk(
  'inboundRoutes/fetchInboundRoutes',
  async () => {
    const servicesManager = getServicesManager()
    const routes = await servicesManager.getInboundRoutes()
    return routes || []
  }
)

export const addInboundRoute = createAsyncThunk(
  'inboundRoutes/addInboundRoute',
  async (routeData: Partial<InboundRoute>) => {
    const servicesManager = getServicesManager()
    const newRoute = await servicesManager.addRoute(routeData)
    return newRoute
  }
)

export const updateInboundRoute = createAsyncThunk(
  'inboundRoutes/updateInboundRoute',
  async ({ routeId, routeData }: { routeId: string; routeData: Partial<InboundRoute> }) => {
    const servicesManager = getServicesManager()
    const success = await servicesManager.updateRoute(routeId, routeData)
    if (success) {
      return { routeId, routeData }
    }
    throw new Error('Failed to update route')
  }
)

export const deleteInboundRoute = createAsyncThunk(
  'inboundRoutes/deleteInboundRoute',
  async (routeId: string) => {
    const servicesManager = getServicesManager()
    const success = await servicesManager.deleteRoute(routeId)
    if (success) {
      return routeId
    }
    throw new Error('Failed to delete route')
  }
)

// State interface
interface InboundRoutesState {
  routes: InboundRoute[]
  selectedRouteId: string | null
  isLoading: boolean
  error: string | null
}

const initialState: InboundRoutesState = {
  routes: [],
  selectedRouteId: null,
  isLoading: false,
  error: null,
}

// Slice
const inboundRoutesSlice = createSlice({
  name: 'inboundRoutes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearRoutes: (state) => {
      state.routes = []
      state.error = null
    },
    setSelectedRoute: (state, action: PayloadAction<string | null>) => {
      state.selectedRouteId = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch routes
      .addCase(fetchInboundRoutes.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchInboundRoutes.fulfilled, (state, action) => {
        state.isLoading = false
        state.routes = action.payload
      })
      .addCase(fetchInboundRoutes.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch routes'
      })
      // Add route
      .addCase(addInboundRoute.fulfilled, (state, action) => {
        if (action.payload) {
          state.routes.push(action.payload)
        }
      })
      .addCase(addInboundRoute.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to add route'
      })
      // Update route
      .addCase(updateInboundRoute.fulfilled, (state, action) => {
        const { routeId, routeData } = action.payload
        const index = state.routes.findIndex(route => route.route_id === routeId)
      if (index !== -1) {
          state.routes[index] = { ...state.routes[index], ...routeData }
  }
      })
      .addCase(updateInboundRoute.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update route'
      })
      // Delete route
      .addCase(deleteInboundRoute.fulfilled, (state, action) => {
        state.routes = state.routes.filter(route => route.route_id !== action.payload)
      })
      .addCase(deleteInboundRoute.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete route'
      })
  },
})

export const { clearError, clearRoutes, setSelectedRoute } = inboundRoutesSlice.actions
export default inboundRoutesSlice.reducer 